import React, { useEffect, useMemo, useState } from 'react';
import { ModalBody, ModalFooter } from '../../../Modal/Modal';

import SpaceAssetsSelectorModalHeader from './SpaceAssetsSelectorModalHeader';
import SpaceAssetsSelectorModalBody from './SpaceAssetsSelectorModalBody';
import SpaceAssetsSelectorModalFooter from './SpaceAssetsSelectorModalFooter';
import CreateFolderDialog from '../CreateFolderDialog';
import { AssetData, FieldConfig, LinkSettings, SpaceSearchOptions } from '../../utils/types';

import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';

import './spaceAssetsModal.scss';
import { useQueryBuilder } from './context/QueryBuilderContext';
import { FILTER_OPERATORS, SPACE_ASSETS_SEARCH_TYPES } from '../../utils/constants';
import { getScreenVariant, SCREENS } from './hooks';
import { getLocaleCodeFromQuery } from './helper';

const capitalizeFirst = (str: string): string => {
  if (!str || typeof str !== 'string') return '';
  return str.substring(0, 1).toUpperCase() + str.substring(1);
};

const formatUserDisplayName = (user: Record<string, any>): string => {
  const name = user.name;
  if (name && typeof name === 'string' && name.trim()) {
    return name;
  }
  const first = user.first_name || user.firstName || '';
  const last = user.last_name || user.lastName || '';
  const firstName = first ? capitalizeFirst(first) + ' ' : '';
  const lastName = last ? capitalizeFirst(last) : '';
  const fullName = (firstName + lastName).trim();
  if (fullName) return fullName;
  return '-';
};

export const getInitialSelectedAssets = (
  preSelectedAssets: AssetData[] | undefined
): AssetData[] => {
  if (preSelectedAssets !== undefined && preSelectedAssets.length > 0) {
    return preSelectedAssets.slice();
  }
  return [];
};

const SpaceAssetsSelectorModalWrapper = (props) => {
  const { 
    isJsonRTE, 
    isInline: isInlineProp, 
    setIsInline: setIsInlineProp, 
    embedAsLink,
    linkSettings: linkSettingsProp,
    setLinkSettings: setLinkSettingsProp,
    hideStackAssetsSwitch, 
    getCurrentBranch, 
    fetchBranch, 
    isVariantPlanEnabled, 
    fetchSpaceAssets,
    fetchSearchSpaces,
    fetchOrgUsers,
    orgUid,
    currentUserUid,
    mode,
    executeMove,
    createFolder: createFolderProp,
    onMoveComplete,
    onMoveError,
    onFilesSelected,
  } = props;

  // creating local state for isInline to avoid stale closure issues with modal body
  const [isInline, setIsInlineLocal] = useState(isInlineProp ?? false);

  // syncing local state changes back to parent
  const setIsInline = (value: boolean) => {
    setIsInlineLocal(value);
    setIsInlineProp?.(value);
  };

  const [linkSettings, setLinkSettingsLocal] = useState<LinkSettings>(linkSettingsProp ?? {
    displayText: '',
    title: '',
    openInNewTab: false
  });

  const setLinkSettings = (settings: LinkSettings) => {
    setLinkSettingsLocal(settings);
    setLinkSettingsProp?.(settings);
  };

  const [userDisplayNameMap, setUserDisplayNameMap] = useState<Record<string, string>>({});
  const [linkedWorkspaces, setLinkedWorkspaces] = useState<{ workspace: string, space_uid: string }[]>([]);
  const [spacesList, setSpacesList] = useState<any[]>([]);
  const [spacesWithTitles, setSpacesWithTitles] = useState<Array<{ uid: string; title: string }>>([]);
  const [spaceAssetsSearchOptions, setSpaceAssetsSearchOptions] = useState<SpaceSearchOptions>({
    searchType: SPACE_ASSETS_SEARCH_TYPES.ALL,
    searchText: ''
  });

  const fetchLinkedWorkspaces = async () => {
    try {
      const currentBranch = getCurrentBranch();
      const { branch } = await fetchBranch({ api_key: props.params.api_key }, { uid: currentBranch, include_settings: true });
      const branchLinkedWorkspaces = branch?.settings?.am_v2?.linked_workspaces || [];
      if (!isEmpty(branchLinkedWorkspaces)) {
        const linkedWorkspaces: { workspace: string, space_uid: string }[] = [];
        const linkedWorkspacesUids: string[] = [];
        branchLinkedWorkspaces.forEach((workspace) => {
          linkedWorkspaces.push({ workspace: workspace.uid, space_uid: workspace.space_uid });
          linkedWorkspacesUids.push(workspace.space_uid);
        })
        setLinkedWorkspaces(linkedWorkspaces);
        setSpacesList(linkedWorkspacesUids);

        await fetchSpacesWithTitles(linkedWorkspacesUids);

        const allUsers: Record<string, string> = {};

        if (fetchOrgUsers && orgUid) {
          try {
            const orgUsersRes = await fetchOrgUsers({
              uid: orgUid,
              include_user_details: true,
              include_count: true,
              limit: 500,
              skip: 0
            });
            const shares = orgUsersRes?.shares || [];
            shares.forEach((user: any) => {
              const uid = user.user_uid || user.uid;
              if (uid) {
                allUsers[uid] = currentUserUid && uid === currentUserUid
                  ? 'Me'
                  : formatUserDisplayName(user);
              }
            });
          } catch (e) {
            console.error('Error fetching org users:', e);
          }
        }

        setUserDisplayNameMap(allUsers);
      }
    } catch (error) {
      console.error('Error fetching linked workspaces:', error);
    }
  }

  const fetchSpacesWithTitles = async (linkedWorkspacesUids: string[]) => {
    if (fetchSearchSpaces && linkedWorkspacesUids.length > 0) {
        try {
          const payload = {
            query: {
              $and: [{
                uid: {
                  $in: linkedWorkspacesUids,
                },
              }],
            },
            object_type: "space",
            fields: ['uid', 'title', 'name']
          };
          const { results = [] } = await fetchSearchSpaces(payload, {});
          const spaceTitles = results.map((space: any) => ({ 
            uid: space.uid, 
            title: space.name || space.title || space.uid 
          }));
          setSpacesWithTitles(spaceTitles);
        } catch (spacesError) {
          console.error('Error fetching space titles:', spacesError);
        }
    }
  }

  useEffect(() => {
    fetchLinkedWorkspaces();
  }, []);

  const { field = {}, closeModal, stackAssetsSwitch } = props || {};
  // Existing Assets Selected State from Entry File Field
  const [totalAssestsSelectedValue, setTotalAssestsSelectedValue] = useState(
    !isEmpty(field?.value) ? field.value.map(file => file.file) : []
  );

  // Get excludeUids when totalAssestsSelectedValue changes
  const excludeUids = useMemo(() => {
    // We should only pick excludeUids from field.value (i.e., from entry file field)
    return !isEmpty(field?.value)
      ? field.value.map((val) => val.file?.uid || '').filter((uid) => uid.startsWith('am'))
      : [];
  }, [field?.value]);

  // Current Assets Picked State
  const [spaceAssetsPicked, setSpaceAssetsPicked] = useState<AssetData[]>([]);

  const updateSpaceAssetsPickedList = (latestPickedList: AssetData[]) => {
    setTotalAssestsSelectedValue(latestPickedList);
  };

  useEffect(() => {
    setTotalAssestsSelectedValue(spaceAssetsPicked);
  }, [spaceAssetsPicked]);

  const isMoveTarget = mode === 'moveTarget';
  const isFolderPickerMode = isMoveTarget || mode === 'uploadTarget';

  const getRootName = () => {
    if (isFolderPickerMode && spacesWithTitles.length > 0) {
      return spacesWithTitles[0].title;
    }
    return 'All assets';
  };

  const rootPath = {
    name: getRootName(),
    root: true,
    isSpaceName: false,
    folder_uid: null,
    action: () => breadcrumbClickHandler({ name: getRootName(), root: true, folder_uid: null }),
    assetData: null
  };
  const [breadcrumbPath, setBreadcrumbPath] = useState([rootPath]);
  const [selectedFolder, setSelectedFolder] = React.useState<AssetData | null>(null);
  const [isSuggestions, setIsSuggestions] = useState(false);
  const [suggestionsSearchSucceeded, setSuggestionsSearchSucceeded] = useState(false);
  const [shouldClearSearchFilters, setShouldClearSearchFilters] = useState(false);
  const [shouldShowInfoComponent, setShouldShowInfoComponent] = useState(true);
  const [isResponsive, setisResponsive] = useState(false);

  const screenVariant = getScreenVariant();
  useEffect(() => {
    if (![SCREENS.XLARGE, SCREENS.XXLARGE].includes(screenVariant)) {
      setisResponsive(true);
    } else {
      setisResponsive(false);
    }
  }, [screenVariant]);

  useEffect(() => {
    if (!isSuggestions) {
      setSuggestionsSearchSucceeded(false);
    }
  }, [isSuggestions]);

  const { setMultipleConditions, queryBuilder } = useQueryBuilder();

  const breadcrumbClickHandler = (obj) => {
    const { name, root, folder_uid, isSpaceName, ...rest } = obj;

    if (root || isSpaceName) {
      setBreadcrumbPath([rootPath]);
      setSelectedFolder(null);
    } else {
      setBreadcrumbPath((prevPath: any) => {
        const indexClicked = findIndex(prevPath, (breadcrumb: any) => breadcrumb.folder_uid === folder_uid);

        if (indexClicked > -1) {
          return prevPath.slice(0, indexClicked + 1);
        } else {
          // In folder picker modes, root crumb IS the space title, so skip adding a separate space name crumb
          if (isFolderPickerMode) {
            return [
              ...prevPath,
              {
                name,
                root: false,
                isSpaceName: false,
                folder_uid,
                action: (clickedObj) => breadcrumbClickHandler(clickedObj || obj),
                assetData: rest
              }
            ];
          }
          // When breadcrumbPath len is 1, we need to add the space name after it, then followed by folders
          if (prevPath.length === 1) {
            return [
              ...prevPath,
              {
                name: rest?.space_name || '',
                root: false,
                isSpaceName: true,
                folder_uid: null,
                action: () => breadcrumbClickHandler({ isSpaceName: true }),
                assetData: null
              },
              {
                name,
                root: false,
                isSpaceName: false,
                folder_uid,
                action: (clickedObj) => breadcrumbClickHandler(clickedObj || obj),
                assetData: rest
              }
            ];
          }
          return [
            ...prevPath,
            {
              name,
              root: false,
              isSpaceName: false,
              folder_uid,
              action: (clickedObj) => breadcrumbClickHandler(clickedObj || obj),
              assetData: rest
            }
          ];
        }
      });
      setSelectedFolder(rest);
    }
  };

  // Handle the Go to Previous Folder, when the current folder is empty
  const handleGoBack = () => {
    if (breadcrumbPath.length > 1) {
      const newPath = breadcrumbPath.slice(0, breadcrumbPath.length - 1);
      setBreadcrumbPath(newPath);
      const lastFolder = newPath[newPath.length - 1];
      if (!lastFolder.root && !lastFolder.isSpaceName) {
        setSelectedFolder(lastFolder.assetData);
      } else {
        setBreadcrumbPath([rootPath]);
        setSelectedFolder(null);
      }
    } else {
      setBreadcrumbPath([rootPath]);
      setSelectedFolder(null);
    }
  };

  const handleStackAssetsSwitch = (e) => {
    e.stopPropagation();
    e.preventDefault();
    closeModal();
    stackAssetsSwitch(e);
  };

  const [messageComponent, setMessageComponent] = useState<React.ReactNode>(null);
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
  const [lastCreatedFolderUid, setLastCreatedFolderUid] = useState<string | null>(null);
  const isNewlyCreatedFolder = lastCreatedFolderUid !== null && selectedFolder?.uid === lastCreatedFolderUid;

  // set inital query builder based on selected folder and linked spaces
  useEffect(() => {
    const fetchAndSetConditions = () => {
      const folderUid = selectedFolder ? (selectedFolder as AssetData).uid : 'root';

      const originalAnd = queryBuilder.query.$and || [];
      const localeFromQuery = getLocaleCodeFromQuery(originalAnd)

      let conditions: Array<{ field: string; operator: string; value: any; shouldRemove?: boolean }> = [];

      if (isFolderPickerMode) {
        // According to the old logic (FolderPickerModal), we only need is_dir and parent_uid.
        // The space scoping is handled by the 'spaces' property in the root of the API payload.
        conditions = [
          {
            field: 'is_dir',
            operator: FILTER_OPERATORS.EQUALS,
            value: true,
          },
          {
            field: 'parent_uid',
            operator: FILTER_OPERATORS.EQUALS,
            value: folderUid,
          }
        ];
      } else {
        // Standard selection logic
        conditions = [
          {
            field: 'uid',
            operator: FILTER_OPERATORS.NOT_IN,
            value: excludeUids || [],
          },
          {
            field: 'locale',
            operator: FILTER_OPERATORS.IN,
            value: localeFromQuery ? localeFromQuery : props.localesWithFallback?.map((locale: any) => locale?.code),
          },
          {
            field: 'parent_uid',
            operator: FILTER_OPERATORS.EQUALS,
            value: folderUid,
            shouldRemove: (!!spaceAssetsSearchOptions.searchText && !selectedFolder) ? true : false,
          },
          {
            field: 'space_uid',
            operator: FILTER_OPERATORS.IN,
            value: !selectedFolder ? linkedWorkspaces.map(ws => ws.space_uid) : [(selectedFolder as AssetData)?.space_uid || ''],
            shouldRemove: !selectedFolder ? true : false,
          }
        ];
      }

      setMultipleConditions(conditions);
    };

    linkedWorkspaces.length > 0 && fetchAndSetConditions();

  }, [selectedFolder, linkedWorkspaces, spaceAssetsSearchOptions]);

  const spaceAssetsModalClassName = [
    'SpaceAssetsModal',
    mode === 'uploadTarget' ? 'SpaceAssetsModal--upload-target' : '',
    mode === 'moveTarget' ? 'SpaceAssetsModal--move-target' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={spaceAssetsModalClassName}>
      <div className={`ReactModal__asset-selector`}>
        {/* Create Folder Dialog — centered popup overlay */}
        {isFolderPickerMode && showCreateFolderDialog && createFolderProp && (
          <CreateFolderDialog
            parentFolderUid={selectedFolder ? selectedFolder.uid : 'root'}
            createFolder={createFolderProp}
            onFolderCreated={(folderUid: string, folderTitle: string) => {
              setShowCreateFolderDialog(false);
              setLastCreatedFolderUid(folderUid);
              // Navigate into the newly created folder
              const crumbs = { name: folderTitle, root: false, folder_uid: folderUid, uid: folderUid, title: folderTitle, is_dir: true };
              breadcrumbClickHandler(crumbs);
            }}
            onCancel={() => setShowCreateFolderDialog(false)}
          />
        )}
        {/* Modal Header */}
        <SpaceAssetsSelectorModalHeader closeModal={closeModal} handleStackAssetsSwitch={handleStackAssetsSwitch} hideStackAssetsSwitch={hideStackAssetsSwitch} mode={mode} />
        {/* Modal Body - Search, Sort & View Select / Assets Listing / InfoComponent */}
        <ModalBody>
          <SpaceAssetsSelectorModalBody
            {...props}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
            breadcrumbPath={breadcrumbPath}
            breadcrumbClickHandler={breadcrumbClickHandler}
            setBreadcrumbPath={setBreadcrumbPath}
            setMessageComponent={setMessageComponent}
            totalAssestsSelectedValue={totalAssestsSelectedValue}
            setTotalAssestsSelectedValue={setTotalAssestsSelectedValue}
            spaceAssetsPicked={spaceAssetsPicked}
            setSpaceAssetsPicked={setSpaceAssetsPicked}
            isSuggestions={isSuggestions}
            setIsSuggestions={setIsSuggestions}
            shouldClearSearchFilters={shouldClearSearchFilters}
            setShouldClearSearchFilters={setShouldClearSearchFilters}
            handleGoBack={handleGoBack}
            excludeUids={excludeUids}
            setShouldShowInfoComponent={setShouldShowInfoComponent}
            spacesList={spacesList}
            componentResponsiveness={{
              isResponsive,
              screenVariant
            }}
            linkedWorkspaces={linkedWorkspaces}
            spacesWithTitles={spacesWithTitles}
            spaceAssetsSearchOptions={spaceAssetsSearchOptions}
            setSpaceAssetsSearchOptions={setSpaceAssetsSearchOptions}
            fetchSpaceAssets={fetchSpaceAssets}
            userDisplayNameMap={userDisplayNameMap}
            isVariantPlanEnabled={isVariantPlanEnabled}
            isJsonRTE={isJsonRTE}
            isInline={isInline}
            setIsInline={setIsInline}
            embedAsLink={embedAsLink}
            linkSettings={linkSettings}
            setLinkSettings={setLinkSettings}
            isNewlyCreatedFolder={isNewlyCreatedFolder}
            onSuggestionsFetchSettled={setSuggestionsSearchSucceeded}
          />

        </ModalBody>
        {/* Modal Footer */}
        <ModalFooter>
          <SpaceAssetsSelectorModalFooter
            {...props}
            totalAssestsSelectedValue={spaceAssetsPicked}
            closeModal={closeModal}
            spaceAsstesPickedCount={spaceAssetsPicked?.length || 0}
            shouldShowInfoComponent={shouldShowInfoComponent}
            messageComponent={messageComponent}
            isResponsive={isResponsive}
            isJsonRTE={isJsonRTE}
            isInline={isInline}
            embedAsLink={embedAsLink}
            linkSettings={linkSettings}
            onCreateFolderClick={() => setShowCreateFolderDialog(true)}
            selectedFolder={selectedFolder}
            showSuggestionsFeedback={isSuggestions && suggestionsSearchSucceeded}
          />
        </ModalFooter>
      </div>
    </div>
  );
};

export default SpaceAssetsSelectorModalWrapper;
